<template>
  <div class="alert alert-danger">
    <h6 class="alert-heading">Danger Zone</h6>
    <label class="form-label fw-semibold">{{
      $t("dashboard.admin_only_transfer_and_delete_company")
    }}</label>

    <div class="bg-body border rounded p-3 mb-3">
      <p class="fw-semibold mb-2">
        <FontAwesomeIcon icon="exclamation-triangle" class="me-2 text-danger" />
        Assets move. Company-level settings do not — check these first, because
        the transfer cannot be undone.
      </p>
      <ul class="small mb-3">
        <li>
          <strong>Portfolio instructions</strong> — minimum prices, allocation
          and delivery settings are dropped wherever the old portfolio is merged
          into one the target already has: a static portfolio of the same type,
          or the target's default portfolio of that type. The certificates move;
          the instructions governing them do not, so they can be sold below the
          price the producer set. Other portfolios are recreated on the target
          with their instructions intact.
        </li>
        <li>
          <strong>Forward pools and deals</strong> — the old company's pool
          memberships, the deals it owns and its producer lines on deliveries
          move to the target. On a delivery that has not shipped, where the
          target already has a line, the two are combined and the target's price
          applies to the whole volume; a shipped delivery keeps both lines as
          history.
        </li>
        <li>
          <strong>Signed agreements</strong> — the signed documents move, but
          the target does not inherit the old company's accepted agreements (its
          legal keys), membership agreement date, contract version or NDA link.
        </li>
        <li>
          <strong>Excluded registry devices</strong> — the target keeps its own
          list, so devices the client asked us not to manage are managed again
          after the next sync.
        </li>
        <li>
          <strong>Other company settings</strong> — fee model, feature flags,
          hosted account regions and sales partner settings all stay as the
          target has them.
        </li>
        <li>
          <strong>Invoicing fee config</strong> — moves to the target only when
          the target has none of its own; otherwise the transfer fails with a
          database error and the old company's fee terms are left behind.
        </li>
        <li>
          <strong>User access</strong> — accounts that belong only to the old
          company lose access to everything that moved.
        </li>
        <li>
          <strong>Registry devices</strong> — if the same device exists on both
          companies, the target ends up with duplicates and double-counted
          production.
        </li>
        <li>
          <strong>History</strong> — the old company's activity log stays with
          the old company; both sides get a <code>company_merged</code> entry
          linking them.
        </li>
      </ul>
      <p class="small mb-0 text-body-secondary">
        The transfer is refused if the old company has any active registry
        connection (soft-deleted ones do not block it), if the merged referrers
        would exceed the count or combined rate cap, or while a forward pool
        that includes all devices has only one of the two companies as a member
        and the other has registered devices, since the merge would add those
        devices to the pool. Switch such a pool to sell from selected devices
        only, or add the other company to it, before transferring.
      </p>
    </div>

    <div class="d-flex gap-2">
      <select
        v-model="selectedTransferCompanyId"
        id="companySelect"
        class="form-select"
      >
        <option value="">Select target company</option>
        <option v-for="target in companies" :key="target.id" :value="target.id">
          {{
            $t("dashboard.transfer_to", {
              name: target.name,
              id: target.id,
            })
          }}
        </option>
      </select>
      <button
        class="btn btn-danger"
        @click="confirmTransfer"
        :disabled="!selectedTransferCompanyId"
      >
        {{ $t("dashboard.transfer") }}
      </button>
    </div>
    <div
      v-if="transferRefusal"
      class="alert alert-warning mt-3 mb-0"
      role="alert"
      data-testid="transfer-refusal"
    >
      {{ transferRefusal }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Company } from "@/types";
import { clientErrorMessage } from "@/utils/apiErrors";
import { ref, watch } from "vue";

const props = defineProps<{
  /** The company whose assets move; it is deleted afterwards. Absent while the account has none. */
  company?: Company;
  /** The account's companies, offered as targets. */
  companies: readonly Company[];
}>();

const { globalErrorHandler } = useGlobalHandlers();

const selectedTransferCompanyId = ref<number | null>(null);
// The backend's reason for refusing the last transfer; the merge's refusals say what to fix first.
const transferRefusal = ref<string | null>(null);

// A refusal describes one source and target pair; once either changes it no longer applies.
watch([selectedTransferCompanyId, () => props.company?.id], () => {
  transferRefusal.value = null;
});

function confirmTransfer() {
  if (!props.company || !selectedTransferCompanyId.value) return;
  const target = props.companies.find(
    (company) => company.id === selectedTransferCompanyId.value
  );
  const confirmed = window.confirm(
    `Are you sure you want to transfer all assets from ${props.company.name}, ID ${props.company.id} ` +
      `to company ${target?.name}, ID: ${selectedTransferCompanyId.value}?`
  );
  if (confirmed) {
    void transferAssets(props.company, selectedTransferCompanyId.value);
  }
}

async function transferAssets(source: Company, targetId: number) {
  transferRefusal.value = null;
  try {
    await goSolidApi.post(
      `/api/admin/company/${source.id}/transfer_assets_and_delete_company/${targetId}`,
      {}
    );
    window.location.reload();
  } catch (error: unknown) {
    // The merge's refusals are worded for the admin: the pools a merge would widen and how to
    // fix them, an active registry connection, the referrer cap.
    const refusal = clientErrorMessage(error);
    if (refusal) {
      transferRefusal.value = refusal;
    } else {
      globalErrorHandler(error);
    }
  }
}
</script>
